package org.teddapp.project.db

import com.tedd.db.Task
import com.tedd.db.TeddDatabase

/**
 * Ce qu'une tâche de parcours déclenche quand elle change d'état, quel que soit
 * l'écran d'où vient le geste : case cochée dans la fiche de compétence, colonne
 * déplacée sur le tableau d'un projet, action forcée depuis Objectifs.
 *
 * Le noyau est branché ici une fois au démarrage ; les dépôts n'ont pas à le connaître.
 */
object TaskHooks {
    /** Appelé quand une feuille de parcours passe à faite ou à refaire. */
    var leafChanged: (suspend (skillId: String, taskId: String, done: Boolean) -> Unit)? = null
}

/**
 * Une tâche est faite si elle est cochée, ou si elle est rangée dans une colonne
 * terminale de son projet. C'est la règle de l'écran Projets ; elle vaut partout.
 */
internal fun isTaskDone(db: TeddDatabase, task: Task): Boolean {
    if (task.status == "done") return true
    val statusId = task.status_id ?: return false
    return db.projectStatusQueries.selectById(statusId).executeAsOneOrNull()?.is_terminal == 1L
}

/** Compétence du parcours dont la tâche est une feuille, ou null si elle n'en est pas une. */
internal fun parcoursSkillOf(db: TeddDatabase, task: Task): String? = when (task.parent_kind) {
    "project" -> task.parent_id?.let { db.parcoursStepQueries.selectByProject(it).executeAsOneOrNull()?.skill_id }
    "milestone" -> task.parent_id
        ?.let { db.milestoneQueries.selectById(it).executeAsOneOrNull() }
        ?.let { db.objectiveQueries.selectById(it.objective_id).executeAsOneOrNull() }
        ?.takeIf { it.step_id != null }
        ?.skill_id
    else -> null
}

/**
 * Compare l'état d'une tâche avant et après un geste, et prévient le crochet si une
 * feuille de parcours a changé de camp. Rien ne part quand rien n'a changé.
 */
internal suspend fun notifyLeafIfChanged(db: TeddDatabase, before: Task) {
    val hook = TaskHooks.leafChanged ?: return
    val after = db.taskQueries.selectByIdAnyState(before.id).executeAsOneOrNull() ?: return
    val wasDone = isTaskDone(db, before)
    val nowDone = isTaskDone(db, after)
    if (wasDone == nowDone) return
    val skill = parcoursSkillOf(db, after) ?: return
    hook(skill, after.id, nowDone)
}
